import type { SxProps, Theme } from '@mui/material';

export const card: SxProps<Theme> = {
  height: '100%',
  backgroundColor: (theme) => theme.palette.background.paper,
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: 2,
  p: 0, // Ensure no padding on the card itself
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: (theme) => theme.shadows[4],
  },
};

export const cardContent: SxProps<Theme> = {
  padding: '16px', // Explicit 16px on all sides to match Figma
  // Use a column flex so the button row always sits at the bottom and rows of
  // cards line up cleanly even when the description is short or empty.
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

export const headerContainer: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 2,
  mb: 2,
};

export const thumbnail: SxProps<Theme> = {
  width: 72,
  height: 72,
  borderRadius: '8px',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  cursor: 'pointer',
};

export const thumbnailContainer: SxProps<Theme> = {
  position: 'relative',
  width: 72,
  height: 72,
  borderRadius: '8px',
  overflow: 'hidden',
  flexShrink: 0,
  '&:hover .thumbnail-overlay': {
    opacity: 1,
  },
};

export const thumbnailOverlay: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.25)',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  pointerEvents: 'none',
};

export const playIcon: SxProps<Theme> = {
  color: (theme) => theme.palette.common.white,
  width: 32,
  height: 32,
  filter: (theme) =>
    theme.palette.mode === 'dark'
      ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.9))'
      : 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
};

export const contentContainer: SxProps<Theme> = {
  flexGrow: 1,
  position: 'relative',
  // Allow this flex child to shrink below its content size so the title can
  // ellipsize when it would otherwise be too wide.
  minWidth: 0,
};

export const metadataContainer: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5, // 4px spacing
  mb: 0.5, // 4px spacing
};

// Wrapper for the collection title
export const titleBox: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  minWidth: 0,
};

export const newChip: SxProps<Theme> = {
  borderColor: (theme) => theme.palette.success.main,
  color: (theme) => theme.palette.success.main,
  backgroundColor: 'transparent',
  fontSize: '12px',
  height: '24px',
  flexShrink: 0,
  transition: 'background-color 0.2s ease, color 0.2s ease',
};

export const newChipFilled: SxProps<Theme> = {
  backgroundColor: (theme) => theme.palette.success.main,
  color: 'white',
  borderColor: 'transparent',
  fontSize: '12px',
  height: '24px',
  flexShrink: 0,
  transition: 'background-color 0.2s ease, color 0.2s ease',
};

export const title: SxProps<Theme> = {
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: 1.6,
  margin: 0,
  // Title fills its flex parent so the ellipsis kicks in at the card's edge.
  width: '100%',
};

export const description: SxProps<Theme> = {
  mb: 2,
  lineHeight: 1.5,
  // Reserve space for the clamped description so all cards in a row have the
  // same height, even when one collection has no description.
  minHeight: 'calc(1.5em * 2)',
};

export const buttonContainer: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
  // Push the button row to the bottom of the card so rows align cleanly.
  mt: 'auto',
};

export const copyButton: SxProps<Theme> = {
  flexGrow: 1,
  color: (theme) => theme.palette.primary.main,
  borderColor: (theme) =>
    theme.palette.mode === 'dark'
      ? theme.palette.primary.main
      : 'rgba(179, 136, 255, 0.5)',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    borderColor: (theme) => theme.palette.primary.main,
    backgroundColor: (theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(179, 136, 255, 0.12)'
        : 'rgba(179, 136, 255, 0.08)',
  },
};

export const viewDetailsButton: SxProps<Theme> = {
  color: (theme) => theme.palette.primary.main,
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: (theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(179, 136, 255, 0.12)'
        : 'rgba(179, 136, 255, 0.08)',
  },
};
